import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import NewsCard from "@/components/base/NewsCard";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((n) => n.id === id);

  useSeo({
    title: article ? `${article.title} - 年轻的母亲2影视资讯` : "资讯未找到 - 年轻的母亲2",
    description: article ? article.summary : "未找到对应的资讯内容，请返回资讯列表查看全部资讯。",
    keywords: article
      ? `${article.category},年轻的母亲2资讯,${article.title}`
      : "年轻的母亲2,影视资讯",
  });

  if (!article) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-article-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这篇资讯</h1>
        <p className="mt-3 text-sm text-foreground-600">该资讯可能已调整或链接有误，返回资讯列表继续浏览。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-50"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList.filter((n) => n.id !== article.id).slice(0, 3);

  return (
    <div className="w-full">
      <PageHero
        eyebrow={article.category}
        title={article.title}
        breadcrumb={[
          { label: "首页", to: "/" },
          { label: "影视资讯", to: "/news" },
          { label: article.title },
        ]}
      >
        <p className="flex items-center gap-2 text-sm text-foreground-600">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-time-line text-base"></i>
          </span>
          发布时间：{article.date}
        </p>
      </PageHero>

      <article className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6 md:py-10">
        <div className="w-full overflow-hidden rounded-lg border border-background-200 bg-secondary-200">
          <div className="h-[220px] w-full sm:h-[320px] md:h-[400px]">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 年轻的母亲2影视资讯`}
              className="h-full w-full object-top"
            />
          </div>
        </div>

        <div className="mt-6">
          <p className="rounded-lg border-l-4 border-accent-500 bg-accent-50 p-4 text-sm leading-relaxed text-foreground-700">
            {article.summary}
          </p>
          <div className="mt-6 flex flex-col gap-5">
            {article.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-8 text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-background-200 pt-5">
          <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900">
            {article.category}
          </span>
          <Link
            to="/news"
            className="inline-flex items-center gap-1 whitespace-nowrap font-label text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line text-base"></i>
            </span>
            返回资讯列表
          </Link>
        </div>

        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            相关文章推荐
          </h2>
          <div className="flex flex-col gap-4">
            {related.map((item) => (
              <NewsCard key={item.id} {...item} />
            ))}
          </div>
        </section>
      </article>
    </div>
  );
}